export const USER_TYPES = ['staff', 'client'] as const;
export type UserType = (typeof USER_TYPES)[number];

export const USER_STATUSES = ['invited', 'active', 'deactivated'] as const;
export type UserStatus = (typeof USER_STATUSES)[number];

export const ORG_TYPES = ['internal', 'client'] as const;
export type OrgType = (typeof ORG_TYPES)[number];

export const PRODUCT_TYPES = ['product', 'service'] as const;
export type ProductType = (typeof PRODUCT_TYPES)[number];

export const PRODUCT_STATUSES = ['active', 'in_development', 'maintenance', 'deprecated'] as const;
export type ProductStatus = (typeof PRODUCT_STATUSES)[number];

export const RELEASE_STATUSES = ['planned', 'in_progress', 'released'] as const;
export type ReleaseStatus = (typeof RELEASE_STATUSES)[number];

export const SUBSCRIPTION_STATUSES = ['active', 'suspended', 'expired'] as const;
export type SubscriptionStatus = (typeof SUBSCRIPTION_STATUSES)[number];

export const SLA_LEVELS = ['standard', 'premium', 'enterprise'] as const;
export type SlaLevel = (typeof SLA_LEVELS)[number];

export const TICKET_TYPES = ['bug', 'complaint', 'change_request', 'query'] as const;
export type TicketType = (typeof TICKET_TYPES)[number];

export const TICKET_PRIORITIES = ['low', 'medium', 'high', 'critical'] as const;
export type TicketPriority = (typeof TICKET_PRIORITIES)[number];

/** The seven ticket lifecycle states. */
export const TICKET_STATUSES = [
  'new',
  'assigned',
  'in_progress',
  'waiting_on_client',
  'resolved',
  'closed',
  'rejected',
] as const;
export type TicketStatus = (typeof TICKET_STATUSES)[number];

export const OPEN_TICKET_STATUSES: TicketStatus[] = ['new', 'assigned', 'in_progress', 'waiting_on_client'];

/**
 * Allowed status transitions. `assigned` is only reached via the assign
 * endpoint (owner + due date required), never via a plain status change.
 */
export const TICKET_TRANSITIONS: Record<TicketStatus, TicketStatus[]> = {
  new: ['rejected'],
  assigned: ['in_progress', 'waiting_on_client', 'resolved', 'rejected'],
  in_progress: ['waiting_on_client', 'resolved'],
  waiting_on_client: ['in_progress', 'resolved'],
  resolved: ['closed', 'in_progress'],
  closed: ['in_progress'],
  rejected: [],
};

/** Transitions a client (reporter side) may perform. */
export const CLIENT_TRANSITIONS: Partial<Record<TicketStatus, TicketStatus[]>> = {
  resolved: ['closed', 'in_progress'],
  closed: ['in_progress'],
};

export const WORK_ITEM_TYPES = ['feature', 'change_request', 'bug_fix', 'improvement'] as const;
export type WorkItemType = (typeof WORK_ITEM_TYPES)[number];

export const WORK_ITEM_STATUSES = ['backlog', 'todo', 'in_progress', 'in_review', 'done'] as const;
export type WorkItemStatus = (typeof WORK_ITEM_STATUSES)[number];

export const LABELS: Record<string, string> = {
  bug: 'Bug',
  complaint: 'Complaint',
  change_request: 'Change request',
  query: 'Query',
  low: 'Low',
  medium: 'Medium',
  high: 'High',
  critical: 'Critical',
  new: 'New',
  assigned: 'Assigned',
  in_progress: 'In progress',
  waiting_on_client: 'Waiting on client',
  resolved: 'Resolved',
  closed: 'Closed',
  rejected: 'Rejected',
  active: 'Active',
  in_development: 'In development',
  maintenance: 'Maintenance',
  deprecated: 'Deprecated',
  product: 'Product',
  service: 'Service',
  planned: 'Planned',
  released: 'Released',
  suspended: 'Suspended',
  expired: 'Expired',
  standard: 'Standard',
  premium: 'Premium',
  enterprise: 'Enterprise',
  feature: 'Feature',
  bug_fix: 'Bug fix',
  improvement: 'Improvement',
  backlog: 'Backlog',
  todo: 'To do',
  in_review: 'In review',
  done: 'Done',
  invited: 'Invited',
  deactivated: 'Deactivated',
  staff: 'Staff',
  client: 'Client',
  internal: 'Internal',
};

export const label = (key: string | null | undefined) => (key ? (LABELS[key] ?? key) : '');
